import React, { useState } from 'react';
import { Button } from '@fieldnation/platform-components';

import styles from './styles.scss';
import { Part, PartUsage, PartEntry, PartsFormV3Props } from './types.d';
import PartCard from './PartCard';
import AddedPartsTable from './AddedPartsTable';
import { nextPartId } from './utils';

const EMPTY_PART: Part = {
  id: 0,
  externalId: '',
  externalNumber: '',
  description: '',
  returnable: '',
  trackingNumber: '',
  carrier: '',
  carrierOther: '',
  returnInstruction: '',
};

const EMPTY_USAGE: PartUsage = {
  usageCode: '',
  customerRetained: '',
  returnTracking: '',
  returnCarrier: '',
  returnCarrierOtherName: '',
  note: '',
};

interface Draft {
  mode: 'add' | 'edit';
  editingId: number | null;
  initialPart: Part;
  initialUsage: PartUsage;
}

const PartsFormV3 = ({ parts, onChange }: PartsFormV3Props): JSX.Element => {
  const [draft, setDraft] = useState<Draft | null>(null);
  const [draftKey, setDraftKey] = useState(0);

  const openDraft = (nextDraft: Draft) => {
    setDraft(nextDraft);
    setDraftKey((key) => key + 1);
  };

  const openAdd = () => {
    openDraft({
      mode: 'add',
      editingId: null,
      initialPart: EMPTY_PART,
      initialUsage: EMPTY_USAGE,
    });
  };

  const openCopy = (id: number) => {
    const entry = parts.find((item) => item.id === id);
    if (!entry) {
      return;
    }
    openDraft({
      mode: 'add',
      editingId: null,
      initialPart: { ...entry.part },
      initialUsage: { ...entry.usage },
    });
  };

  const openEdit = (id: number) => {
    const entry = parts.find((item) => item.id === id);
    if (!entry) {
      return;
    }
    openDraft({
      mode: 'edit',
      editingId: id,
      initialPart: { ...entry.part },
      initialUsage: { ...entry.usage },
    });
  };

  const handleDelete = (id: number) => {
    onChange(parts.filter((entry) => entry.id !== id));
  };

  const handleSave = (part: Part, usage: PartUsage, addAnother: boolean) => {
    if (draft?.mode === 'edit' && draft.editingId !== null) {
      onChange(parts.map((entry) => (
        entry.id === draft.editingId ? { ...entry, part, usage } : entry
      )));
    } else {
      const newEntry: PartEntry = {
        id: nextPartId(parts),
        part,
        usage,
      };
      onChange([...parts, newEntry]);
    }

    if (addAnother) {
      openAdd();
    } else {
      setDraft(null);
    }
  };

  return (
    <div>
      {parts.length > 0 && (
        <AddedPartsTable
          parts={parts}
          showAddPart={!draft}
          onAddPart={openAdd}
          onEdit={openEdit}
          onCopy={openCopy}
          onDelete={handleDelete}
        />
      )}

      {parts.length === 0 && !draft && (
        <div className={styles['add-part-button']}>
          <Button
            id="addPartButton"
            label="Add part"
            icon="icon-plus"
            type="link"
            onClick={openAdd}
          />
        </div>
      )}

      {draft && (
        <PartCard
          key={draftKey}
          initialPart={draft.initialPart}
          initialUsage={draft.initialUsage}
          isEditing={draft.mode === 'edit'}
          onCancel={() => setDraft(null)}
          onSave={handleSave}
          className={parts.length > 0 ? styles['card-below-table'] : undefined}
        />
      )}
    </div>
  );
};

export default PartsFormV3;
